import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import { FaqAccordionItem } from "./FAQ";
import { gsap } from "gsap";
import CRAFT_FAQS from "../content/craftFaqs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Arrow() {
  return (
    <svg
      width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17L17 7M17 7H8M17 7V16" />
    </svg>
  );
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  bg:          "radial-gradient(130% 64% at 50% -12%, rgba(123,181,177,0.10) 0%, transparent 58%), #08090A",
  rowHover:    "#0C0F10",
  heading:     "#FFFFFF",
  body:        "#AEB6B6",
  secondary:   "#8B9495",
  muted:       "#5C6466",
  index:       "#3B4143",
  border:      "rgba(255,255,255,0.10)",
  mint:        "#A6DAD2",
  mintLine:    "#6FB6AE",
  mintBright:  "#C2E8E1",
  mintTint:    "rgba(120,181,177,0.14)",
  glyphMint:   "#8FD3CB",
};

// ── Capability taxonomy (design constant) ─────────────────────────────────────
const CAPABILITIES = [
  { key: "agents",          label: "Agent Architecture", accent: "#6366F1" },
  { key: "retrieval",       label: "Retrieval / RAG",    accent: "#3B82F6" },
  { key: "automation",      label: "Automation",         accent: "#16A34A" },
  { key: "evals",           label: "Evals",              accent: "#A855F7" },
  { key: "content",         label: "Content AI",         accent: "#CA8A04" },
  { key: "personalization", label: "Personalization",    accent: "#0284C7" },
  { key: "ai_consulting",   label: "AI Consulting",      accent: "#F59E0B" },
  { key: "ai_product",      label: "AI Product Mgmt",    accent: "#EC4899" },
  { key: "ai_dev",          label: "AI Development",     accent: "#10B981" },
  { key: "agentic_os",      label: "Agentic OS",         accent: "#EF4444" },
  { key: "model_training",  label: "Model Training",     accent: "#14B8A6" },
  { key: "inference",       label: "Inference",          accent: "#06B6D4" },
  { key: "web_mobile",      label: "Web & Mobile",       accent: "#F97316" },
  { key: "cloud_infra",     label: "Cloud Infra",        accent: "#84CC16" },
  { key: "data_eng",        label: "Data Engineering",   accent: "#94A3B8" },
];
const CAP_MAP = Object.fromEntries(CAPABILITIES.map(c => [c.key, c]));

// ── Glyph registry (inline SVG) ───────────────────────────────────────────────
function Glyph({ type, size = 24, color = T.glyphMint }) {
  const paths = {
    tree: (
      <>
        <circle cx="12" cy="5" r="2.2"/>
        <circle cx="6" cy="17" r="2.2"/>
        <circle cx="18" cy="17" r="2.2"/>
        <line x1="12" y1="7.2" x2="7" y2="14.8"/>
        <line x1="12" y1="7.2" x2="17" y2="14.8"/>
      </>
    ),
    funnel: <path d="M3.5 5h17l-6.5 7.5v5l-4-2v-3L3.5 5z"/>,
    page: (
      <>
        <rect x="4" y="2" width="13" height="18" rx="1.5"/>
        <line x1="7.5" y1="8" x2="13.5" y2="8"/>
        <line x1="7.5" y1="11.5" x2="13.5" y2="11.5"/>
        <line x1="7.5" y1="15" x2="11" y2="15"/>
      </>
    ),
    scale: (
      <>
        <line x1="12" y1="3" x2="12" y2="20"/>
        <line x1="5" y1="7" x2="19" y2="7"/>
        <path d="M5 7a3 3 0 0 0 6 0"/>
        <path d="M13 7a3 3 0 0 0 6 0"/>
        <line x1="8" y1="20" x2="16" y2="20"/>
      </>
    ),
    doc: (
      <>
        <path d="M6 2h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
        <path d="M14 2v4h4"/>
        <line x1="8" y1="10" x2="14" y2="10"/>
        <line x1="8" y1="14" x2="14" y2="14"/>
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="9"/>
        <circle cx="12" cy="12" r="5"/>
        <circle cx="12" cy="12" r="1.5"/>
      </>
    ),
    stack: (
      <>
        <rect x="3" y="15" width="18" height="4" rx="1"/>
        <rect x="5" y="10" width="14" height="4" rx="1"/>
        <rect x="7" y="5" width="10" height="4" rx="1"/>
      </>
    ),
    handoff: (
      <>
        <circle cx="6" cy="12" r="2.5"/>
        <line x1="8.5" y1="12" x2="15" y2="12"/>
        <polyline points="12,8.5 16,12 12,15.5"/>
        <circle cx="18" cy="12" r="2.5"/>
      </>
    ),
  };
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={1.7}
      strokeLinecap="round" strokeLinejoin="round"
    >
      {paths[type] || paths.doc}
    </svg>
  );
}

// ── Tiny helpers ──────────────────────────────────────────────────────────────
function CategoryLabel({ capKey }) {
  const cap = CAP_MAP[capKey];
  if (!cap) return null;
  return (
    <span style={{
      fontFamily: "monospace",
      fontSize: "10.5px",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: cap.accent,
    }}>
      {cap.label}
    </span>
  );
}

const TIER_LABELS = { 1: "Foundational", 2: "Composite", 3: "Advanced" };

function TierPill({ level }) {
  return (
    <span style={{
      display: "inline-flex", flexShrink: 0,
      borderRadius: 9999, border: "1px solid rgba(255,255,255,0.22)",
      color: "rgba(255,255,255,0.6)",
      fontFamily: "Montserrat, sans-serif", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.02em", padding: "5px 12px", whiteSpace: "nowrap",
    }}>
      {TIER_LABELS[level] || ""}
    </span>
  );
}

function RefTag({ children }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 99,
      border: `1px solid ${T.border}`,
      fontSize: "10.5px",
      color: T.secondary,
      fontFamily: "monospace",
      whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

function Rise({ delay = 0, children }) {
  const [shown, setShown] = useState(prefersReducedMotion);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(id);
  }, []);
  return (
    <div style={{
      opacity: shown ? 1 : 0,
      transform: shown ? "none" : "translateY(14px)",
      transition: "opacity .5s cubic-bezier(.25,.46,.45,.94), transform .5s cubic-bezier(.25,.46,.45,.94)",
    }}>
      {children}
    </div>
  );
}

function GlyphTile({ glyphType, accent }) {
  return (
    <div style={{
      width: 42, height: 42, flexShrink: 0, position: "relative",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "absolute", inset: "-34%", borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}52 0%, transparent 68%)`,
        pointerEvents: "none",
      }}/>
      <div style={{ filter: `drop-shadow(0 0 5px ${accent}8C)` }}>
        <Glyph type={glyphType} size={28} color={T.glyphMint}/>
      </div>
    </div>
  );
}

// ── RecipeCard ────────────────────────────────────────────────────────────────
function RecipeCard({ pattern, index, dimmed }) {
  const [expanded, setExpanded] = useState(false);
  const cap = CAP_MAP[pattern.cap] || { accent: T.glyphMint, label: "" };

  return (
    <Rise delay={index * 50}>
      <div
        style={{
          position: "relative", height: "100%", boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
          border: `1px solid ${expanded ? "rgba(91,148,144,0.55)" : "rgba(91,148,144,0.18)"}`,
          borderRadius: 10,
          overflow: "hidden",
          padding: "22px 24px",
          transition: "border-color 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease",
          boxShadow: expanded
            ? "0 0 36px rgba(91,148,144,0.26), 0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 0 22px rgba(91,148,144,0.13), 0 4px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
          opacity: dimmed ? 0.38 : 1,
          filter: dimmed ? "saturate(0.6)" : "none",
          pointerEvents: dimmed ? "none" : "auto",
        }}
      >
        {/* Left accent rule */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: 3, background: T.glyphMint,
          transform: expanded ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "top",
          transition: "transform 0.35s ease",
          boxShadow: expanded ? `0 0 12px ${cap.accent}99` : "none",
        }}/>

        {/* Top row — index + tier */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: 28, fontWeight: 300, color: T.index }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <TierPill level={pattern.complexity} />
        </div>

        {/* Glyph + title + capability */}
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginTop: 14 }}>
          <GlyphTile glyphType={pattern.glyph} accent={cap.accent}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              margin: 0,
              fontFamily: "Montserrat, sans-serif",
              fontSize: "18px", fontWeight: 600,
              letterSpacing: "-0.02em", color: T.heading, lineHeight: 1.25,
            }}>
              {pattern.name}
            </h3>
            <div style={{ marginTop: 6 }}>
              <CategoryLabel capKey={pattern.cap}/>
            </div>
          </div>
        </div>

        {/* Deployed / Serves stats */}
        <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
          {pattern.deployedIn && (
            <div>
              <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Deployed</div>
              <div style={{ marginTop: 3 }}><RefTag>{pattern.deployedIn}</RefTag></div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Serves</div>
            <div style={{ marginTop: 3 }}><RefTag>{pattern.serves}</RefTag></div>
          </div>
        </div>

        {/* Summary */}
        <p style={{ margin: "14px 0 0", fontSize: "14px", color: T.body, lineHeight: 1.55 }}>
          {pattern.solves}
        </p>

        {/* Expand to read the mechanism */}
        <button
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          style={{
            display: "flex", alignItems: "center", gap: 6, marginTop: 16,
            background: "none", border: "none", padding: 0, cursor: "pointer",
            color: T.glyphMint, fontFamily: "Montserrat, sans-serif", fontSize: 13, fontWeight: 500,
          }}
        >
          Read the full breakdown
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.glyphMint}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        <div style={{
          display: "grid",
          gridTemplateRows: expanded ? "1fr" : "0fr",
          transition: "grid-template-rows 0.35s ease",
        }}>
          <div style={{ overflow: "hidden", minHeight: 0 }}>
            <p style={{
              margin: "12px 0 2px", paddingTop: 12, borderTop: `1px solid ${T.border}`,
              fontSize: "13.5px", color: T.secondary, lineHeight: 1.6,
            }}>
              {pattern.how}
            </p>
          </div>
        </div>
      </div>
    </Rise>
  );
}

// ── Coming-soon placeholder tile ─────────────────────────────────────────────
function ComingSoonCard() {
  return (
    <div style={{
      border: "1.5px dashed rgba(255,255,255,0.15)", borderRadius: 10, padding: 24,
      display: "flex", flexDirection: "column", alignItems: "flex-start",
      minHeight: 220, boxSizing: "border-box",
    }}>
      <span style={{ width: 40, height: 40, borderRadius: 9999, background: "rgba(255,255,255,0.04)", display: "grid", placeItems: "center" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
        </svg>
      </span>
      <h4 style={{
        fontFamily: "Montserrat, sans-serif", fontSize: 15, fontWeight: 500,
        color: "rgba(255,255,255,.55)", margin: "16px 0 0",
      }}>
        New recipe in the works
      </h4>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,.45)", margin: "6px 0 0", lineHeight: 1.5 }}>
        Check back soon.
      </p>
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────
function FilterBar({ activeTechnique, onTechnique }) {
  const chipStyle = (active) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 14px", borderRadius: 99, cursor: "pointer",
    border: active
      ? "1px solid rgba(91,148,144,0.85)"
      : "1px solid rgba(91,148,144,0.22)",
    fontSize: "12px", fontFamily: "Montserrat, sans-serif", fontWeight: 500,
    background: active
      ? "linear-gradient(135deg, rgba(91,148,144,0.28) 0%, rgba(45,92,90,0.18) 100%)"
      : "linear-gradient(135deg, rgba(91,148,144,0.06) 0%, rgba(45,92,90,0.04) 100%)",
    color: active ? "#a8dbd8" : T.secondary,
    boxShadow: active
      ? "0 0 12px rgba(91,148,144,0.35), 0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.12)"
      : "0 1px 4px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)",
    textShadow: active ? "0 0 8px rgba(123,181,177,0.5)" : "none",
    transition: "all 0.18s",
  });

  const TECHNIQUES = [
    { val: 0, label: "Any" },
    { val: 1, label: "Foundational" },
    { val: 2, label: "Composite" },
    { val: 3, label: "Advanced" },
  ];

  return (
    <div style={{
      padding: "16px 0",
      marginTop: 38,
      display: "flex", gap: 6, flexWrap: "wrap",
      justifyContent: "flex-end",
    }}>
      {TECHNIQUES.map(t => (
        <button key={t.val} style={chipStyle(activeTechnique === t.val)} onClick={() => onTechnique(t.val)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{
      border: `1px solid ${T.border}`, borderRadius: 10, padding: "22px 24px",
      boxSizing: "border-box",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ width: 24, height: 22, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}/>
        <div style={{ width: 70, height: 20, background: "rgba(255,255,255,0.06)", borderRadius: 99 }}/>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 18 }}>
        <div style={{ width: 42, height: 42, background: "rgba(255,255,255,0.06)", borderRadius: 8 }}/>
        <div style={{ width: 160, height: 18, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}/>
      </div>
      <div style={{ width: "90%", height: 13, background: "rgba(255,255,255,0.06)", borderRadius: 3, marginTop: 22 }}/>
      <div style={{ width: "70%", height: 13, background: "rgba(255,255,255,0.06)", borderRadius: 3, marginTop: 8 }}/>
    </div>
  );
}

// ── CrossRef strip ────────────────────────────────────────────────────────────
function CrossRef() {
  return (
    <div style={{
      marginTop: 56, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.08)",
      textAlign: "center",
    }}>
      <p style={{
        margin: "0 0 16px", fontSize: "16px", fontWeight: 300,
        color: "rgba(255,255,255,0.6)", fontFamily: "Montserrat, sans-serif",
      }}>
        Have a problem that doesn't match a recipe yet?
      </p>
      <button
        onClick={() => { window.location.href = "/#contact"; }}
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: "13px",
          fontWeight: 500,
          color: "#fff",
          background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
          border: "1px solid rgba(91,148,144,0.55)",
          borderRadius: "7px",
          padding: "8px 18px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
          e.currentTarget.style.boxShadow = "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
          e.currentTarget.style.boxShadow = "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        Start from a need
      </button>
    </div>
  );
}

// ── DUMMY_PATTERNS fallback ───────────────────────────────────────────────────
const DUMMY_PATTERNS = [
  {
    id: "1", cap: "agents", glyph: "tree", name: "Supervisor–Subagent Mesh",
    solves: "Orchestrate specialised agents without a monolithic prompt.",
    how: "A supervisor LLM decomposes tasks and routes sub-tasks to domain agents via tool calls. Each agent owns a narrow context window and returns structured results. The supervisor synthesises, retries on failure, and decides when the goal is met.",
    complexity: 3, deployedIn: "NeuroSync", serves: "AI Platforms", displayOrder: 1, isVisible: true,
  },
  {
    id: "2", cap: "retrieval", glyph: "funnel", name: "Contextual Chunk Re-Ranker",
    solves: "Improve retrieval precision without enlarging the context window.",
    how: "After a first-pass vector search, a cross-encoder re-ranks the top-N chunks by query relevance. Only the top-K survive into the prompt. Reduces hallucination on long documents and keeps latency under 200 ms at p95.",
    complexity: 2, deployedIn: null, serves: "AI Platforms", displayOrder: 2, isVisible: true,
  },
  {
    id: "3", cap: "automation", glyph: "stack", name: "Event-Driven Trigger Chain",
    solves: "Automate multi-step workflows without polling or cron jobs.",
    how: "Webhook events land in a queue; a lightweight consumer maps event types to handler functions. Handlers are idempotent and retry-safe. Dead-letter queues capture failures for manual triage.",
    complexity: 2, deployedIn: "Verdant", serves: "Enterprise Tooling", displayOrder: 3, isVisible: true,
  },
  {
    id: "4", cap: "evals", glyph: "scale", name: "Dual-Rubric Output Scorer",
    solves: "Quantify LLM output quality without expensive human annotation at scale.",
    how: "Two rubrics — a task rubric (correctness, completeness) and a safety rubric (refusals, hallucinations) — are applied by a judge LLM to each output. Scores are logged with the input hash, enabling regression tests across model updates.",
    complexity: 2, deployedIn: null, serves: "Model Teams", displayOrder: 4, isVisible: true,
  },
  {
    id: "5", cap: "content", glyph: "doc", name: "Brand-Voice Persona Distiller",
    solves: "Generate on-brand copy at scale without per-generation human review.",
    how: "A style guide is distilled into a compact persona prompt via few-shot examples drawn from approved content. The persona is injected as a system message and periodically re-evaluated against new approved samples to detect drift.",
    complexity: 1, deployedIn: null, serves: "Content Teams", displayOrder: 5, isVisible: true,
  },
  {
    id: "6", cap: "personalization", glyph: "target", name: "Latent User-Intent Classifier",
    solves: "Surface relevant content without requiring explicit user preferences.",
    how: "Implicit signals (dwell time, clicks, session path) are encoded into a latent preference vector. A lightweight classifier maps the vector to one of N intent clusters. Content feeds are filtered and re-ranked per cluster at query time.",
    complexity: 3, deployedIn: "EchoCommerce", serves: "Consumer Apps", displayOrder: 6, isVisible: true,
  },
  {
    id: "7", cap: "agents", glyph: "handoff", name: "Tool-Call Handoff Protocol",
    solves: "Pass work cleanly between agents with full context and typed contracts.",
    how: "Each agent exposes a typed tool schema. Handoffs are explicit tool-call messages, not freeform text. The receiving agent validates the payload against its schema before execution, rejecting malformed inputs rather than silently hallucinating a fix.",
    complexity: 2, deployedIn: null, serves: "AI Platforms", displayOrder: 7, isVisible: true,
  },
  {
    id: "8", cap: "retrieval", glyph: "page", name: "Hybrid Sparse-Dense Retriever",
    solves: "Recover exact-match terms that dense embeddings routinely miss.",
    how: "BM25 and vector search run in parallel; results are merged with Reciprocal Rank Fusion. Sparse search dominates for proper nouns and rare terms; dense search covers semantic synonyms. The blend is tunable per use case with a single α parameter.",
    complexity: 2, deployedIn: null, serves: "AI Platforms", displayOrder: 8, isVisible: true,
  },
];

// ── Process framework ─────────────────────────────────────────────────────────

const PROCESS_STEPS = [
  {
    num: "01", title: "Discover & Define", accent: "#7BB5B1",
    desc: "Clarify the highest-value opportunities and establish a shared product vision - anchored in real user problems, not vendor assumptions.",
    panel: "Workshops where your team helps write the PRD, not just review it.",
    icon: "discover",
  },
  {
    num: "02", title: "Design & Architect", accent: "#818CF8",
    desc: "Translate requirements into validated designs and a production-ready technical architecture before a single line of code is committed.",
    panel: "We walk every wireframe and architecture call through with your team first.",
    icon: "design",
  },
  {
    num: "03", title: "Build & Validate", accent: "#34D399",
    desc: "Engineers and AI specialists build scalable, production-grade systems with continuous QA, evals, and iteration baked in from sprint one.",
    panel: "Sprint demos and staging access - you watch it run, not just hear about it.",
    icon: "build",
  },
  {
    num: "04", title: "Launch & Scale", accent: "#F59E0B",
    desc: "Turn a working product into a market-winning one - from GTM execution to post-launch monitoring and continuous optimisation.",
    panel: "Shared dashboards after launch. We stay accountable to the numbers with you.",
    icon: "launch",
  },
];

// ── Process panel icons (animated, matches the step being viewed) ─────────────
function ProcessIcon({ type }) {
  const common = { width: 72, height: 72, viewBox: "0 0 24 24", fill: "none", stroke: "#7BB5B1", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  if (type === "discover") {
    return (
      <svg {...common}>
        <path d="M3 6h13" opacity=".35" /><path d="M3 10.5h9" opacity=".35" /><path d="M3 15h11" opacity=".35" />
        <g className="gx-icon-scan"><circle cx="9" cy="10.5" r="4.5" /><path d="M12.2 13.7l3.3 3.3" /></g>
      </svg>
    );
  }
  if (type === "design") {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="16" rx="1.5" />
        <path d="M3 9h18" />
        <rect className="gx-icon-block1" x="5.5" y="11.5" width="5.5" height="6" />
        <rect className="gx-icon-block2" x="13" y="11.5" width="5.5" height="6" />
      </svg>
    );
  }
  if (type === "build") {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="13" rx="1.5" /><path d="M8 20h8" /><path d="M12 17v3" />
        <text className="gx-icon-type1" x="5.3" y="9.6" fontSize="4" fill="#7BB5B1" stroke="none" fontFamily="monospace">01</text>
        <text className="gx-icon-type2" x="5.3" y="14.6" fontSize="4" fill="#7BB5B1" stroke="none" fontFamily="monospace">10</text>
        <rect className="gx-icon-cursor" x="14" y="12.4" width="1.6" height="3.2" fill="#7BB5B1" stroke="none" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <g className="gx-icon-rocket">
        <path className="gx-icon-flame" d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      </g>
      <circle className="gx-icon-smoke1" cx="9" cy="21.5" r="1.1" fill="#7BB5B1" stroke="none" />
      <circle className="gx-icon-smoke2" cx="12.5" cy="22" r="0.9" fill="#7BB5B1" stroke="none" />
    </svg>
  );
}

const PROCESS_STEP_MS = 4000;

function ProcessFramework() {
  const [visible, setVisible] = useState(prefersReducedMotion);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const restartTimer = () => {
    clearInterval(timerRef.current);
    if (prefersReducedMotion) return;
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % PROCESS_STEPS.length);
    }, PROCESS_STEP_MS);
  };

  useEffect(() => {
    if (!visible) return;
    restartTimer();
    return () => clearInterval(timerRef.current);
  }, [visible]);

  const selectStep = (i) => {
    setCurrent(i);
    restartTimer();
  };

  return (
    <div ref={ref} style={{ marginTop: 72, marginBottom: 16 }}>
      <style>{`
        @keyframes gx-icon-scan{0%,100%{transform:translate(-1px,0) rotate(-4deg)}50%{transform:translate(6px,0) rotate(4deg)}}
        .gx-icon-scan{animation:gx-icon-scan 2.8s ease-in-out infinite;transform-origin:center}
        @keyframes gx-block-in{0%,100%{opacity:0;transform:scale(.6)}20%,80%{opacity:1;transform:scale(1)}}
        .gx-icon-block1{animation:gx-block-in 3s ease-in-out infinite;transform-origin:center}
        .gx-icon-block2{animation:gx-block-in 3s ease-in-out infinite .5s;transform-origin:center}
        @keyframes gx-type-in{0%{opacity:0}10%{opacity:1}80%{opacity:1}95%,100%{opacity:0}}
        .gx-icon-type1{animation:gx-type-in 3s ease-in-out infinite}
        .gx-icon-type2{animation:gx-type-in 3s ease-in-out infinite .5s}
        @keyframes gx-cursor-blink{0%,49%{opacity:1}50%,100%{opacity:0}}
        .gx-icon-cursor{animation:gx-cursor-blink 1s steps(1) infinite}
        @keyframes gx-launch-ascend{0%{transform:translateY(0);opacity:1}70%{transform:translateY(-9px);opacity:1}85%,100%{transform:translateY(-14px);opacity:0}}
        .gx-icon-rocket{animation:gx-launch-ascend 2.4s cubic-bezier(.4,0,1,1) infinite}
        @keyframes gx-flame{0%,100%{opacity:.5}50%{opacity:1}}
        .gx-icon-flame{animation:gx-flame .4s ease-in-out infinite}
        @keyframes gx-smoke-rise{0%{transform:translateY(0) scale(.6);opacity:0}20%{opacity:.5}60%{transform:translateY(-6px) scale(1.3);opacity:.3}100%{transform:translateY(-10px) scale(1.6);opacity:0}}
        .gx-icon-smoke1,.gx-icon-smoke2{animation:gx-smoke-rise 2.4s ease-out infinite;transform-origin:center}
        .gx-icon-smoke2{animation-delay:.4s}
        @media (prefers-reduced-motion: reduce){.gx-icon-scan,.gx-icon-block1,.gx-icon-block2,.gx-icon-type1,.gx-icon-type2,.gx-icon-cursor,.gx-icon-rocket,.gx-icon-flame,.gx-icon-smoke1,.gx-icon-smoke2{animation:none}}
        @media (max-width: 767px) {
          .process-grid { grid-template-columns: 1fr !important; }
          .process-steps-list { display: none !important; }
          .process-mobile-nav { display: flex !important; }
        }
      `}</style>

      {/* Section header */}
      <div style={{
        marginBottom: 36,
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(14px)",
        transition: "opacity 0.55s ease, transform 0.55s ease",
      }}>
        <h2 style={{
          fontFamily: "Montserrat, sans-serif", fontWeight: 300,
          fontSize: "clamp(24px, 3.5vw, 38px)", letterSpacing: "-0.025em",
          color: T.heading, margin: "0 0 12px", lineHeight: 1.1,
        }}>
          Our Craft
        </h2>
        <p style={{
          fontSize: "clamp(12px, 1.5vw, 14px)", fontWeight: 300, fontFamily: "Montserrat, sans-serif",
          color: "rgba(255,255,255,0.40)", lineHeight: 1.7, margin: 0,
        }}>
          A disciplined process from first principles to production. Designed to minimise risk, move fast, and build things that last.
        </p>
      </div>

      <div className="process-grid" style={{
        display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 48,
        alignItems: "center",
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(22px)",
        transition: "opacity 0.65s cubic-bezier(.25,.46,.45,.94), transform 0.65s cubic-bezier(.25,.46,.45,.94)",
      }}>
        {/* Left: numbered step list — desktop only; mobile gets the compact dot nav below the icon panel instead, to keep the page short */}
        <div className="process-steps-list" style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {PROCESS_STEPS.map((step, i) => {
            const active = i === current;
            const passed = i < current;
            return (
              <button
                key={step.num}
                type="button"
                onClick={() => selectStep(i)}
                aria-current={active ? "step" : undefined}
                style={{
                  display: "flex", gap: 16, alignItems: "flex-start", cursor: "pointer",
                  opacity: active ? 1 : 0.45, transition: "opacity .4s ease",
                  background: "none", border: "none", padding: 0, margin: 0,
                  width: "100%", textAlign: "left", font: "inherit",
                }}
              >
                <span style={{
                  width: 36, height: 36, borderRadius: 9999, flexShrink: 0,
                  display: "grid", placeItems: "center",
                  border: `1px solid ${active ? "rgba(123,181,177,.65)" : passed ? "rgba(91,148,144,.5)" : "rgba(255,255,255,.15)"}`,
                  background: active
                    ? "linear-gradient(150deg,#3f7a76,#2D5C5A)"
                    : passed
                    ? "linear-gradient(150deg,rgba(91,148,144,0.28),rgba(45,92,90,0.14))"
                    : "linear-gradient(150deg,rgba(255,255,255,0.05),rgba(255,255,255,0.015))",
                  boxShadow: active
                    ? "0 6px 16px -4px rgba(45,92,90,.7), inset 0 1px 1px rgba(255,255,255,.2)"
                    : passed
                    ? "inset 0 1px 2px rgba(0,0,0,.3), 0 2px 6px rgba(0,0,0,.25)"
                    : "inset 0 1px 2px rgba(0,0,0,.4)",
                  color: active ? "#fff" : passed ? "#7BB5B1" : "rgba(255,255,255,.5)",
                  transform: active ? "scale(1.1)" : "scale(1)",
                  transition: "all .3s cubic-bezier(0.4,0,0.2,1)",
                }}>
                  {passed ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  ) : (
                    <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: 13, fontWeight: 700 }}>{step.num}</span>
                  )}
                </span>
                <div>
                  <h3 style={{
                    fontFamily: "Montserrat, sans-serif", fontSize: 17, fontWeight: 600,
                    letterSpacing: "-0.025em", color: T.heading, margin: 0,
                  }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,.6)", lineHeight: 1.6, margin: "6px 0 0" }}>
                    {step.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right: animated illustration panel */}
        <div style={{
          position: "relative", minHeight: 320, borderRadius: 12, background: "#000",
          overflow: "hidden", border: "1px solid rgba(255,255,255,.08)", perspective: 900,
        }}>
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(circle at 75% 20%,rgba(91,148,144,0.16),transparent 45%),radial-gradient(circle at 25% 85%,rgba(45,92,90,0.14),transparent 45%)",
          }} />
          {PROCESS_STEPS.map((step, i) => {
            const active = i === current;
            const before = i < current;
            return (
              <div key={step.num} style={{
                opacity: active ? 1 : 0,
                transform: active ? "translateY(0) rotateX(0deg)" : before ? "translateY(-36px) rotateX(10deg)" : "translateY(36px) rotateX(-10deg)",
                transition: "opacity .5s ease, transform .6s cubic-bezier(0.25,0.46,0.45,0.94)",
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", textAlign: "center", padding: 28,
                pointerEvents: active ? "auto" : "none",
              }}>
                <span style={{ width: 112, height: 112, display: "grid", placeItems: "center", marginBottom: 8 }}>
                  <ProcessIcon type={step.icon} />
                </span>
                <h3 style={{
                  fontFamily: "Montserrat, sans-serif", fontSize: 20, fontWeight: 500,
                  letterSpacing: "-0.025em", color: "#fff", margin: "8px 0 0",
                }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,.55)", margin: "8px 0 0", maxWidth: 280 }}>
                  {step.panel}
                </p>
              </div>
            );
          })}
        </div>

        {/* Mobile-only step nav: dots replace the full text list to keep the page short, but stay a real focusable control so reduced-motion / keyboard users can still advance */}
        <div className="process-mobile-nav" style={{ display: "none", justifyContent: "center", gap: 10, marginTop: 4 }}>
          {PROCESS_STEPS.map((step, i) => {
            const active = i === current;
            return (
              <button
                key={step.num}
                type="button"
                onClick={() => selectStep(i)}
                aria-current={active ? "step" : undefined}
                aria-label={`Go to step ${i + 1}: ${step.title}`}
                style={{
                  width: active ? 22 : 8, height: 8, borderRadius: 9999,
                  border: "none", padding: 0, cursor: "pointer",
                  background: active ? "#7BB5B1" : "rgba(255,255,255,.2)",
                  transition: "all .3s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Craft page ───────────────────────────────────────────────────────────
export default function Craft() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTechnique, setActiveTechnique] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const heroHeadingRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !heroHeadingRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(heroHeadingRef.current, {
        clipPath: "inset(0 100% 0 0)", duration: 0.9, ease: "power3.out",
      });
    });
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    axios.get(`${API}/craft`)
      .then(res => {
        if (res.data.success) setPatterns(res.data.data);
        else setPatterns(DUMMY_PATTERNS);
      })
      .catch(() => setPatterns(DUMMY_PATTERNS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <div style={{ background: T.bg, minHeight: "100vh", color: T.body }}>
        <div style={{
          padding: "128px clamp(24px,6vw,80px) 48px",
        }}>

          {/* Breadcrumb */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 13, color: "rgba(255,255,255,.5)",
            fontFamily: "Montserrat, sans-serif", marginBottom: 24,
          }}>
            <Link to="/" style={{ color: "rgba(255,255,255,.5)", textDecoration: "none" }}>Home</Link>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
            <span style={{ color: "#fff", fontWeight: 500 }} aria-current="page">Craft</span>
          </div>

          {/* Hero */}
          <div style={{ maxWidth: 780 }}>
            <h1 ref={heroHeadingRef} style={{
              fontFamily: "Montserrat, sans-serif", fontWeight: 300,
              fontSize: "clamp(36px,5vw,64px)", color: T.heading,
              letterSpacing: -2, lineHeight: 1.1, margin: 0,
            }}>
              Craft & <span style={{ color: "#7BB5B1" }}>Recipes</span>
            </h1>
          </div>
          <p style={{
            marginTop: 20, fontSize: "clamp(13px, 3.6vw, 15px)", fontWeight: 300,
            color: "rgba(255,255,255,0.45)", lineHeight: 1.8,
            fontFamily: "Montserrat, sans-serif",
          }}>
            Battle-tested frameworks behind every production system we ship. Built from first principles, not vendor playbooks.
          </p>
          {/* Process framework */}
          <ProcessFramework />

          {/* Recipes section label + description */}
          <h2 style={{
            fontFamily: "Montserrat, sans-serif", fontWeight: 300,
            fontSize: "clamp(24px, 3.5vw, 38px)", letterSpacing: "-0.025em",
            color: T.heading, margin: "56px 0 0", lineHeight: 1.1,
          }}>
            Recipes
          </h2>
          <p style={{
            marginTop: 12, fontSize: "clamp(12px, 1.5vw, 14px)", fontStyle: "italic",
            fontWeight: 300, color: T.muted, lineHeight: 1.6,
            fontFamily: "Montserrat, sans-serif",
          }}>
            We don't document everything we can build - only the patterns we'd stake a project on. Each one here has earned its place in production.
          </p>

          {/* Filter bar — shown once data is ready */}
          {!loading && (
            <FilterBar
              activeTechnique={activeTechnique}
              onTechnique={setActiveTechnique}
            />
          )}

          {/* Cards */}
          <style>{`
            @media (max-width: 767px) { .recipes-grid { grid-template-columns: 1fr !important; } }
          `}</style>
          <div className="recipes-grid" style={{
            marginTop: 8, display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)", gap: 24,
          }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : patterns.map((p, i) => {
                  const dimmed = activeTechnique !== 0 && p.complexity !== activeTechnique;
                  return <RecipeCard key={p.id} pattern={p} index={i} dimmed={dimmed} />;
                })
            }
            {!loading && activeTechnique === 0 && <ComingSoonCard />}
          </div>

          {/* CrossRef + FooterNote — shown once loaded */}
          {!loading && <CrossRef />}

          {/* FAQ */}
          <div style={{ marginTop: 96 }}>
            <h2 style={{
              fontFamily: "Montserrat, sans-serif", fontWeight: 300,
              fontSize: "clamp(24px, 3.5vw, 38px)", letterSpacing: "-0.025em",
              color: T.heading, margin: "0 0 32px", lineHeight: 1.1,
            }}>
              How We <span style={{ color: "#7BB5B1" }}>Work/FAQ</span>
            </h2>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
              {CRAFT_FAQS.map((item, i) => (
                <FaqAccordionItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </div>

          {/* Closing CTA banner */}
          <div style={{
            marginTop: 96, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10, padding: "64px 48px", textAlign: "center",
          }}>
            <h2 style={{
              fontFamily: "Montserrat, sans-serif", fontWeight: 300,
              fontSize: "clamp(24px,3vw,40px)", letterSpacing: "-0.025em",
              color: "#fff", margin: 0,
            }}>
              Bring us your hardest problem.
            </h2>
            <p style={{
              fontSize: 16, fontWeight: 300, color: "rgba(255,255,255,.6)",
              maxWidth: 480, margin: "12px auto 32px", lineHeight: 1.7,
              fontFamily: "Montserrat, sans-serif",
            }}>
              We scope from your need, not our template — then build it as a partnership from day one.
            </p>
            <button
              onClick={() => { window.location.href = "/#contact"; }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 9,
                padding: "15px 26px", borderRadius: "8px",
                border: "1px solid rgba(91,148,144,0.55)",
                background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
                color: "#FFFFFF",
                fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 16,
                letterSpacing: "-0.01em", cursor: "pointer",
                boxShadow: "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)";
              }}
            >
              Start Your Project <Arrow />
            </button>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
}
